// 업무 자료함 API — /api/v2/library/*  (스크린챗 티켓 #87)
//
// 매장 공용 파일·링크 보관함. 권한 그룹 없이 로그인만 요구하고(서버 requireAuth),
// 격리는 매장(tenant)으로만 한다 — 같은 매장 직원은 같은 자료를 본다.

import { authJson, authForm } from "./_client";

const API = `${import.meta.env.VITE_API_BASE_URL ?? ""}/api/v2/library`;

export type LibraryKind = "file" | "link" | "note";

export interface LibraryItem {
  id: number;
  kind: LibraryKind;
  title: string;
  description: string | null;
  /** kind='link' 일 때의 주소 */
  url: string | null;
  /** kind='file' 일 때의 원본 파일명 */
  fileName: string | null;
  contentType: string | null;
  sizeBytes: number;
  folderId: number | null;
  createdAt: string;
}

export interface LibraryFolder {
  id: number;
  name: string;
  sortOrder: number;
  /** 이 분류의 링크를 안내창 없이 새 탭으로 열어도 되는가 */
  directOpen: boolean;
  /** 그 분류에 든 자료 수 */
  count: number;
}

export interface LibraryFolderList {
  folders: LibraryFolder[];
  /** 분류가 안 붙은 자료 수 */
  uncategorized: number;
}

export const libraryQk = {
  folders: ["library", "folders"] as const,
  items: (folderId: string, q: string) => ["library", "items", folderId, q] as const,
  search: (q: string) => ["library", "search", q] as const,
};

// ── 분류(폴더) ──────────────────────────────────────────

export function fetchLibraryFolders(): Promise<LibraryFolderList> {
  return authJson<LibraryFolderList>(`${API}/folders`);
}

export function createLibraryFolder(name: string): Promise<{ id: number }> {
  return authJson(`${API}/folders`, {
    method: "POST",
    body: JSON.stringify({ name }),
  });
}

export function renameLibraryFolder(id: number, name: string): Promise<{ ok: true }> {
  return authJson(`${API}/folders/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ name }),
  });
}

export function setLibraryFolderDirectOpen(
  id: number,
  directOpen: boolean,
): Promise<{ ok: true }> {
  return authJson(`${API}/folders/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ directOpen }),
  });
}

/** 분류만 없앤다 — 안에 있던 자료는 미분류로 남는다(서버가 그렇게 처리). */
export function deleteLibraryFolder(id: number): Promise<{ ok: true }> {
  return authJson(`${API}/folders/${id}`, { method: "DELETE" });
}

// ── 자료 ────────────────────────────────────────────────

/** 목록 — folderId 는 숫자 문자열 / "none"(미분류) / ""(전체). */
export function fetchLibraryItems(
  folderId: string,
  q: string,
): Promise<{ items: LibraryItem[] }> {
  const p = new URLSearchParams();
  if (folderId) p.set("folderId", folderId);
  if (q.trim()) p.set("q", q.trim());
  const qs = p.toString();
  return authJson(`${API}/items${qs ? `?${qs}` : ""}`);
}

/** 통합검색(상단 검색창) — 최대 20건. 빈 검색어면 서버가 빈 목록을 준다. */
export function searchLibrary(q: string): Promise<{ items: LibraryItem[] }> {
  return authJson(`${API}/search?q=${encodeURIComponent(q.trim())}`);
}

export function createLibraryLink(input: {
  title: string;
  url: string;
  description?: string;
  folderId?: number | null;
}): Promise<LibraryItem> {
  return authJson(`${API}/items/link`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function createLibraryNote(input: {
  title: string;
  content: string;
  folderId?: number | null;
}): Promise<LibraryItem> {
  return authJson(`${API}/items/note`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function uploadLibraryFile(input: {
  file: File;
  title?: string;
  description?: string;
  folderId?: number | null;
}): Promise<LibraryItem> {
  const form = new FormData();
  form.append("file", input.file);
  if (input.title) form.append("title", input.title);
  if (input.description) form.append("description", input.description);
  if (input.folderId != null) form.append("folderId", String(input.folderId));
  return authForm<LibraryItem>(`${API}/items/file`, form);
}

export function updateLibraryItem(
  id: number,
  patch: {
    title?: string;
    description?: string | null;
    url?: string;
    folderId?: number | null;
  },
): Promise<{ ok: true }> {
  return authJson(`${API}/items/${id}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export function deleteLibraryItem(id: number): Promise<{ ok: true }> {
  return authJson(`${API}/items/${id}`, { method: "DELETE" });
}

/** 글자 파일 본문 — 서버가 저장 방식(UTF-8/옛 CP949)을 판별해 UTF-8 로 변환한 결과다. */
export interface LibraryItemText {
  text: string;
  /** 원본이 어떤 방식으로 저장돼 있었나 (`cp949` 면 옛 메모장 파일) */
  encoding: string;
  /** 너무 길어 앞부분만 보여주는 중인가 */
  truncated: boolean;
}

export function fetchLibraryItemText(id: number): Promise<LibraryItemText> {
  return authJson<LibraryItemText>(`${API}/items/${id}/text`);
}

/**
 * 자료 1건을 "여는" 단일 진입 — 검색 결과에서든 자료함 목록에서든 이 함수 하나만 부른다.
 * PDF·이미지는 인라인 URL, 글자 파일은 본문을 받아 화면이 직접 그리고, 나머지는 내려받기로 연다.
 *
 * 링크는 안전하다고 미리 지정한 폴더만 새 탭으로 열고(스크린챗 티켓 #204), 그 밖에는
 * `privateLink` 를 돌려 "시크릿 창에 붙여넣기" 안내로 처리한다(티켓 #162). 설정을 모르는
 * 폴더와 미분류는 항상 안전한 안내 경로로 떨어진다.
 *
 * 파일은 서명 URL 을 받아 와야 하지만 새 창이 아니라 a[download] 흐름이라 팝업 차단 대상이 아니다 —
 * 서명에 attachment 가 구워져 있어 탭 이동 없이 원래 이름으로 저장된다.
 *
 * 글자 파일을 iframe 에 직접 물리지 않는 이유: 브라우저가 UTF-8 로만 읽어 옛 윈도우 메모장
 * (CP949) 파일이 통째로 깨져 보였다(스크린챗 티켓 #140). 지금은 서버가 변환해 준 본문을 쓴다.
 */
export async function openLibraryItem(
  item: LibraryItem,
  opts?: { directOpenFolderIds?: ReadonlySet<number> },
): Promise<{ previewUrl?: string; text?: LibraryItemText; privateLink?: string }> {
  if (item.kind === "link") {
    if (
      item.url &&
      item.folderId != null &&
      opts?.directOpenFolderIds?.has(item.folderId)
    ) {
      window.open(item.url, "_blank", "noopener");
      return {};
    }
    return item.url ? { privateLink: item.url } : {};
  }
  if (item.kind === "note") return {};
  const { url, mode } = await authJson<{
    url?: string;
    mode: "preview" | "download" | "text";
  }>(`${API}/items/${item.id}/open`);
  if (mode === "text") return { text: await fetchLibraryItemText(item.id) };
  if (mode === "preview") return { previewUrl: url };
  triggerDownload(url!);
  return {};
}

/**
 * 자료를 **원본 그대로** 내 컴퓨터에 저장한다 — 원래 파일 이름으로, 바이트 변환 없이.
 * (미리보기는 옛 한글 파일을 UTF-8 로 바꿔 보여주지만, 저장되는 파일은 손대지 않는다.)
 */
export async function downloadLibraryItem(item: LibraryItem): Promise<void> {
  const { url } = await authJson<{ url: string }>(
    `${API}/items/${item.id}/open?download=1`,
  );
  triggerDownload(url);
}

/** 서명 URL 에 attachment 가 구워져 있어, 링크를 눌러도 탭 이동 없이 저장만 된다. */
function triggerDownload(url: string): void {
  const a = document.createElement("a");
  a.href = url;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** 바이트 → 사람이 읽는 크기. */
export function formatBytes(bytes: number): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
